import { useContext, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { caseQuickUpdate } from "../../lib/fetchApi"
import { getCaseIdFromCasePayload } from "../../lib/caseUpdateHelpers"
import { notificationActions } from "../../store/notifications"
import { caseActions } from "../../store/case"
import { WorkQueueUpdateContext } from "../work_queues/pages/drawer/WorkQueueUpdateContext"
import { shouldShowFieldUpdateLoader } from "../../lib/systemData"
import CustomSelect from "./interactive/CustomSelect"
import InputBoxCurrency from "./interactive/InputBoxCurrency"
import InputBox from "./interactive/InputBox"
import RadioField from "./interactive/RadioField"
import CheckboxCell from "./interactive/CheckboxCell"
import DatepickerWithLabel from "./interactive/DatepickerWithLabel"
import TextareaBox from "./interactive/TextareaBox"

export default function DefaultUpdater({type, value, label, name, identifier, options, extraOptions, extraAction, merge_before_update}) {

    const dispatch = useDispatch() 
    const activeCase = useSelector(state => state.case.activeCase) 
    const userToken = useSelector(state => state.user.token)
    const workQueueUpdate = useContext(WorkQueueUpdateContext)
    const workQueueSlug = workQueueUpdate?.slug

    const [pendingRequests, setPendingRequests] = useState(0)
    const requestSequenceRef = useRef(0)

    const processUpdate = async (data) => {
        setPendingRequests(prevState => prevState + 1)
        const prevValue = data?.meta?.prevValue
        let reqBody = {
            type: data.identifier.type,
            type_id: data.identifier.type_id,
            fields: {
                [data.identifier.field] : data.data.value,
            }
        }
        if (workQueueSlug) {
            reqBody.workq = workQueueSlug
        }
 
        if (merge_before_update) { // use this to send simple modifications in the same request
            if (merge_before_update.items) {
                merge_before_update.items.forEach(item => {
                    if (data.data.value == item.value_flag) { // usualy it has some catch for example in some cases the value needs to be true

                        if (item.fields_to_modify) {
                            item.fields_to_modify.forEach(element => {
                                reqBody.fields[element.field] = element.value
                            });
                        }
                    } else if (item.value_flag == 'in_all_cases') {
                        if (item.fields_to_modify) {
                            item.fields_to_modify.forEach(element => {
                                reqBody.fields[element.field] = element.value
                            });
                        }
                    }
                })
            }            
        }

        // NOTE:
        // Avoid synchronous optimistic case merges on blur. They can trigger large rerenders
        // and cause noticeable lag when moving quickly between editable fields.

        const caseId = getCaseIdFromCasePayload(activeCase)
        if (caseId == null) {
            dispatch(notificationActions.setError({ errorMessage: 'Case is not available to save changes.', type: 'error' }))
            setPendingRequests(prevState => Math.max(0, prevState - 1))
            return
        }

        const fieldKey = `${data.identifier.type}:${data.identifier.type_id}:${data.identifier.field}`
        const requestSequence = requestSequenceRef.current + 1
        requestSequenceRef.current = requestSequence

        try {
            let update = await caseQuickUpdate(userToken, caseId, reqBody, dispatch, workQueueSlug ? true : null, {
                staleGuardKey: `${caseId}:${fieldKey}`,
                staleGuardSequence: requestSequence,
            })

            if (update && update.errorMessage) {
                dispatch(notificationActions.setError(update))
                // If we optimistically updated this exact case field, revert it
                // only when this request is still the latest for the field.
                if (
                    data?.meta?.optimisticCaseField &&
                    prevValue !== undefined &&
                    requestSequenceRef.current === requestSequence
                ) {
                    dispatch(caseActions.batchUpdate({
                        case: {
                            [data.identifier.field]: prevValue,
                        },
                    }))
                }
                // Revert optimistic item-folder update when this request is still the latest.
                if (
                    data?.meta?.optimisticItemField &&
                    prevValue !== undefined &&
                    requestSequenceRef.current === requestSequence
                ) {
                    dispatch(caseActions.modifyActiveCase({
                        folder: data.identifier.folder,
                        item: {
                            itemId: data.identifier.type_id,
                            field: data.identifier.field,
                            value: prevValue,
                        },
                    }))
                }
                return
            }        

            // Ignore aborted/stale responses (a newer update for the same field is in-flight).
            if (update?.staleResponse) {
                return
            }

            // Keep Redux in sync after save. fetchApi may merge case_data, but quick-update
            // responses often omit singleton folders (e.g. case_police_report).
            const folder = data?.identifier?.folder
            const field = data?.identifier?.field
            if (folder && field) {
                if (folder === 'case') {
                    dispatch(caseActions.batchUpdate({
                        case: {
                            [field]: data.data.value,
                        },
                    }))
                } else if (data.identifier.type_id == null) {
                    const fields = Object.entries(reqBody.fields).map(([f, v]) => ({
                        field: f,
                        value: v,
                    }))
                    dispatch(caseActions.modifyActiveCaseMultipleObject({
                        folder,
                        item: { fields },
                    }))
                }
            }

            if (extraAction) {
                extraAction(data)
            }
        } finally {
            setPendingRequests(prevState => Math.max(0, prevState - 1))
        }
        
    }
    
    const updateHandler = (data) => {
        // For select-like quick updates on case fields (notably case_type_id),
        // update Redux immediately so dependent UI (tabs) reacts without waiting
        // for the network roundtrip. If the request fails, processUpdate will revert.
        const shouldOptimisticallyUpdateCaseField =
            data?.identifier?.folder === 'case' &&
            typeof data?.identifier?.field === 'string' &&
            data.identifier.field.length > 0

        if (shouldOptimisticallyUpdateCaseField) {
            data.meta = {
                ...(data.meta ?? {}),
                optimisticCaseField: true,
                prevValue: value,
            }
            dispatch(caseActions.batchUpdate({
                case: {
                    [data.identifier.field]: data.data.value,
                },
            }))
        }

        // Opt-in optimistic update for item-folder fields (e.g. mp_pip_um insurance_state) so
        // dependent UI (like PIP fields toggling on the selected state) reacts immediately.
        const shouldOptimisticallyUpdateItemField =
            extraOptions?.optimistic &&
            data?.identifier?.folder &&
            data.identifier.folder !== 'case' &&
            data?.identifier?.type_id != null &&
            typeof data?.identifier?.field === 'string' &&
            data.identifier.field.length > 0

        if (shouldOptimisticallyUpdateItemField) {
            data.meta = {
                ...(data.meta ?? {}),
                optimisticItemField: true,
                prevValue: value,
            }
            dispatch(caseActions.modifyActiveCase({
                folder: data.identifier.folder,
                item: {
                    itemId: data.identifier.type_id,
                    field: data.identifier.field,
                    value: data.data.value,
                },
            }))
        }

        // Let the next focus/click event complete before heavy optimistic update work.
        setTimeout(() => {
            processUpdate(data)
        }, 0)
    }


    const apiDisabled = pendingRequests > 0
    const showApiLoader = extraOptions?.forceShowApiLoader === true || shouldShowFieldUpdateLoader()

    return <>
        {type == 'checkbox' && <CheckboxCell action={updateHandler} label={label} checked={value} name={name} identifier={identifier} {...extraOptions} apiDisabled={apiDisabled} showApiLoader={showApiLoader}/>}
        {type == 'select' && <CustomSelect action={updateHandler} label={label} optionsData={options} preSelectedValue={value} name={name} identifier={identifier} {...extraOptions} fixedDrawer={extraOptions?.fixedDrawer ?? true} apiDisabled={apiDisabled} showApiLoader={showApiLoader}/>}
        {type == 'curency' && <InputBoxCurrency action={updateHandler} label={label} currentValue={value} identifier={identifier} name={name} {...extraOptions} apiDisabled={apiDisabled} showApiLoader={showApiLoader}/>}
        {type == 'input' && <InputBox action={updateHandler} label={label} currentValue={value} name={name} identifier={identifier} {...extraOptions} apiDisabled={apiDisabled} showApiLoader={showApiLoader}/>}
        {type == 'radio' && <RadioField action={updateHandler} legend={label} fields={options} name={name} defaultChecked={value} identifier={identifier} {...extraOptions} apiDisabled={apiDisabled} showApiLoader={showApiLoader}/>}
        {type == 'date' && <DatepickerWithLabel action={updateHandler} label={label} name={name} defaultDate={value} identifier={identifier} {...extraOptions} apiDisabled={apiDisabled} showApiLoader={showApiLoader}/>}
        {type == 'textarea' && <TextareaBox action={updateHandler} label={label} name={name} currentValue={value} identifier={identifier} {...extraOptions} apiDisabled={apiDisabled} showApiLoader={showApiLoader}/>}
    </>

}